import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCancelSource,
  fetchLogs,
  isCancel,
  sendMessage as sendMessageApi,
  uploadAttachment as uploadAttachmentApi,
} from '../api';
import { openCreativeChat, relayCreativeMessage } from '../../../../../services/creativeChatSocket';

const sortLogs = (logs) =>
  [...logs].sort((a, b) => {
    const t = Number(a?.created_time || 0) - Number(b?.created_time || 0);
    if (t !== 0) return t;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

const mergeLogs = (existing, incoming) => {
  const list = Array.isArray(incoming) ? incoming : [incoming];
  const clean = list.filter(Boolean);
  if (!clean.length) return existing;

  const seen = new Set(existing.map((l) => String(l?.id)));
  const added = clean.filter((l) => {
    const key = String(l?.id);
    if (l?.id == null || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!added.length) return existing;
  return sortLogs([...existing, ...added]);
};

/**
 * The CST admin panel's own conversation feed for one creative.
 *
 * Unlike the sibling content-writer panel, this admin panel has no `/timeline`
 * endpoint — the shared `/logs` endpoint is the only feed there is. So this
 * hook reads the FULL log list (no filtering by log type, unlike
 * `useCreativeRequests`, which reads the same endpoint but only cares about
 * request rows) and renders it oldest-first as a thread.
 *
 * `sendMessage` / `sendAttachment` fold the log rows their own responses return
 * straight into local state via `mergeLogs`, so a post never needs a refetch.
 * There is deliberately no re-entrancy guard on either action: the consuming
 * `ThreadPanel` already owns a `submittingRef` lock, and duplicating it here
 * would only make the two locks drift.
 */
const useAdminCreativeThread = (bscId, { enabled = true } = {}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const reqRef = useRef(0);
  const sourceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (sourceRef.current) sourceRef.current.cancel('component unmounted');
    };
  }, []);

  const load = useCallback(async () => {
    if (!bscId) return;
    const reqId = reqRef.current + 1;
    reqRef.current = reqId;
    if (sourceRef.current) sourceRef.current.cancel('superseded');
    const source = createCancelSource();
    sourceRef.current = source;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLogs(bscId, source.token);
      if (!mountedRef.current || reqId !== reqRef.current) return;
      setLogs(sortLogs(Array.isArray(data) ? data : []));
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current || reqId !== reqRef.current) return;
      setError(err?.message || 'Unable to load the conversation.');
    } finally {
      if (mountedRef.current && reqId === reqRef.current) setLoading(false);
    }
  }, [bscId]);

  useEffect(() => {
    if (!enabled || !bscId) {
      setLogs([]);
      return undefined;
    }
    load();
    return () => {
      if (sourceRef.current) sourceRef.current.cancel('creative changed');
    };
  }, [enabled, bscId, load]);

  // Live relay join. Admin has no status gate on messaging either (`canSend`
  // is hardcoded true in this panel's ThreadPanel, and admin's config.js has
  // no `canMessage` gate at all), so join whenever the thread is open.
  useEffect(() => {
    if (!enabled || !bscId) return undefined;
    return openCreativeChat(bscId, {
      onMessage: (log) => setLogs((prev) => mergeLogs(prev, log)),
    });
  }, [enabled, bscId]);

  /**
   * Fold externally-obtained log row(s) into this hook's own `logs` state —
   * exposed so a sibling that reads the same `/logs` feed (e.g. the Requests
   * tab's approve/reject) can write into this one authoritative cache instead
   * of keeping its own, which is what let the two tabs go out of sync.
   */
  const mergeIncomingLogs = useCallback((incoming) => {
    setLogs((prev) => mergeLogs(prev, incoming));
  }, []);

  /** Post a plain text message. Resolves `true` on success, `false` otherwise. */
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || !bscId) return false;
      try {
        const data = await sendMessageApi(bscId, trimmed);
        if (!mountedRef.current) return false;
        setLogs((prev) => mergeLogs(prev, data?.logs));
        (data?.logs || []).forEach((log) => relayCreativeMessage(bscId, log));
        return true;
      } catch (err) {
        if (mountedRef.current) setError(err?.message || 'Unable to send your message.');
        return false;
      }
    },
    [bscId]
  );

  /** Upload one file with an optional caption. Resolves `true` on success. */
  const sendAttachment = useCallback(
    async (file, caption, onUploadProgress) => {
      if (!file || !bscId) return false;
      try {
        const data = await uploadAttachmentApi({
          bsc_id: bscId,
          file,
          message: String(caption || '').trim(),
          onUploadProgress,
        });
        if (!mountedRef.current) return false;
        setLogs((prev) => mergeLogs(prev, data?.logs));
        (data?.logs || []).forEach((log) => relayCreativeMessage(bscId, log));
        return true;
      } catch (err) {
        if (mountedRef.current) setError(err?.message || 'Upload failed. Please try again.');
        return false;
      }
    },
    [bscId]
  );

  return { logs, loading, error, reload: load, sendMessage, sendAttachment, mergeIncomingLogs };
};

export default useAdminCreativeThread;
